import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import './TechnicianPage.css';

interface ServiceRequest {
  id: string;
  customer_id: string;
  service_type: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_year: number | null;
  license_plate: string;
  location: {
    address: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  } | null;
  status: string;
  technician_id?: string;
  created_at: string;
  updated_at: string;
  customer?: {
    id: string;
    email: string;
    full_name?: string;
    phone?: string;
  };
}

interface TechnicianProfile {
  id: string;
  services_offered: string[];
  availability: boolean;
  rating: number;
  completed_jobs: number;
}

export const TechnicianPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'available' | 'assigned'>('available');
  const [availableRequests, setAvailableRequests] = useState<ServiceRequest[]>([]);
  const [assignedRequests, setAssignedRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [technicianProfile, setTechnicianProfile] = useState<TechnicianProfile | null>(null);

  // Fetch technician profile
  useEffect(() => {
    const fetchTechnicianProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('technicians')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
          console.error('Error fetching technician profile:', error);
          return;
        }

        // If no technician record exists, create one
        if (!data) {
          const { data: newTechnician, error: createError } = await supabase
            .from('technicians')
            .insert([{
              id: user.id,
              services_offered: ['tire_change', 'battery_jump', 'lockout', 'fuel_delivery', 'towing', 'other'],
              availability: true,
              rating: 0,
              completed_jobs: 0
            }])
            .select()
            .single();

          if (createError) {
            console.error('Error creating technician profile:', createError);
            return;
          }

          setTechnicianProfile(newTechnician);
        } else {
          setTechnicianProfile(data);
        }
      } catch (error) {
        console.error('Error fetching technician profile:', error);
      }
    };

    fetchTechnicianProfile();
  }, [user]);

  // Fetch service requests
  useEffect(() => {
    const fetchServiceRequests = async () => {
      if (!user) return;

      setLoading(true);
      try {

        // Fetch available requests (pending status, no technician assigned)
        const { data: availableData, error: availableError } = await supabase
          .from('service_requests')
          .select('*')
          .eq('status', 'pending')
          .is('technician_id', null)
          .order('created_at', { ascending: false });


        if (availableError) {
          console.error('Error fetching available requests:', availableError);
        } else {
          setAvailableRequests(availableData || []);
        }

        // Fetch assigned requests (assigned to this technician)
        const { data: assignedData, error: assignedError } = await supabase
          .from('service_requests')
          .select('*')
          .eq('technician_id', user.id)
          .in('status', ['in_progress', 'completed'])
          .order('created_at', { ascending: false });

        if (assignedError) {
          console.error('Error fetching assigned requests:', assignedError);
        } else {
          setAssignedRequests(assignedData || []);
        }
      } catch (error) {
        console.error('Error fetching service requests:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceRequests();
  }, [user]);

  const handleAssignRequest = async (requestId: string) => {
    if (!user) return;

    setActionLoading(requestId);
    try {
      const { error } = await supabase
        .from('service_requests')
        .update({
          technician_id: user.id,
          status: 'in_progress',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) {
        console.error('Error assigning request:', error);
        alert('Failed to assign request. Please try again.');
        return;
      }

      // Refresh the lists
      const updatedAvailable = availableRequests.filter(req => req.id !== requestId);
      setAvailableRequests(updatedAvailable);

      // Find the assigned request and add to assigned list
      const assignedRequest = availableRequests.find(req => req.id === requestId);
      if (assignedRequest) {
        setAssignedRequests(prev => [{
          ...assignedRequest,
          technician_id: user.id,
          status: 'in_progress'
        }, ...prev]);
      }

      alert('Request assigned successfully!');
    } catch (error) {
      console.error('Error assigning request:', error);
      alert('Failed to assign request. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateStatus = async (requestId: string, newStatus: 'completed') => {
    setActionLoading(requestId);
    try {
      const { error } = await supabase
        .from('service_requests')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) {
        console.error('Error updating request status:', error);
        alert('Failed to update request status. Please try again.');
        return;
      }

      // Update the assigned requests list
      setAssignedRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: newStatus }
            : req
        )
      );

      alert(`Request status updated to ${newStatus.replace('_', ' ')}!`);
    } catch (error) {
      console.error('Error updating request status:', error);
      alert('Failed to update request status. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const getServiceTypeIcon = (serviceType: string) => {
    const icons: { [key: string]: string } = {
      tire_change: 'fas fa-tire',
      battery_jump: 'fas fa-battery-quarter',
      lockout: 'fas fa-key',
      fuel_delivery: 'fas fa-gas-pump',
      towing: 'fas fa-truck',
      other: 'fas fa-tools'
    };
    return icons[serviceType] || 'fas fa-tools';
  };

  const getServiceTypeName = (serviceType: string) => {
    const names: { [key: string]: string } = {
      tire_change: 'Tire Change',
      battery_jump: 'Battery Jump',
      lockout: 'Lockout Service',
      fuel_delivery: 'Fuel Delivery',
      towing: 'Towing',
      other: 'Other Service'
    };
    return names[serviceType] || 'Other Service';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!user) {
    return (
      <div className="technician-page">
        <div className="technician-container">
          <div className="access-denied">
            <h2>Access Denied</h2>
            <p>Please log in to access the technician dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!technicianProfile) {
    return (
      <div className="technician-page">
        <div className="technician-container">
          <div className="access-denied">
            <h2>Technician Profile Required</h2>
            <p>You need to set up your technician profile to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="technician-page">
      <div className="technician-header">
        <h1 className="technician-title">Technician Dashboard</h1>
      </div>
      
      <div className="technician-container">
        <div className="technician-info-card">
          <div className="technician-details">
            <span className="technician-name">Technician</span>
            <span className={`status-badge ${technicianProfile.availability ? 'available' : 'unavailable'}`}>
              {technicianProfile.availability ? 'Available' : 'Unavailable'}
            </span>
          </div>
          <div className="technician-stats">
            <div className="stat-item">
              <span className="stat-value">{technicianProfile.rating.toFixed(1)}</span>
              <span className="stat-label">Rating</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{technicianProfile.completed_jobs}</span>
              <span className="stat-label">Jobs Completed</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{technicianProfile.services_offered.length}</span>
              <span className="stat-label">Services Offered</span>
            </div>
          </div>
        </div>

        <div className="technician-tabs">
          <button
            className={`tab-button ${activeTab === 'available' ? 'active' : ''}`}
            onClick={() => setActiveTab('available')}
          >
            <i className="fas fa-list"></i>
            Available Requests ({availableRequests.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'assigned' ? 'active' : ''}`}
            onClick={() => setActiveTab('assigned')}
          >
            <i className="fas fa-user-check"></i>
            My Assignments ({assignedRequests.length})
          </button>
        </div>

        <div className="requests-content">

          {loading ? (
            <div className="loading-state">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading requests...</span>
            </div>
          ) : (
            <>
              {activeTab === 'available' && (
                <div className="available-requests">
                  <h2>Available Service Requests</h2>
                  {availableRequests.length === 0 ? (
                    <div className="empty-state">
                      <i className="fas fa-check-circle"></i>
                      <p>No available requests at the moment.</p>
                      <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                        Service requests will appear here when customers submit them through the app.
                      </p>
                    </div>
                  ) : (
                    <div className="requests-grid">
                      {availableRequests.map((request) => (
                        <div key={request.id} className="request-card">
                          <div className="request-header">
                            <div className="service-type">
                              <i className={getServiceTypeIcon(request.service_type)}></i>
                              <span>{getServiceTypeName(request.service_type)}</span>
                            </div>
                          </div>

                          <div className="request-details">
                            <div className="detail-row">
                              <i className="fas fa-car"></i>
                              <span>{request.vehicle_year} {request.vehicle_make} {request.vehicle_model}</span>
                            </div>
                            <div className="detail-row">
                              <i className="fas fa-id-card"></i>
                              <span>License: {request.license_plate}</span>
                            </div>
                            <div className="detail-row">
                              <i className="fas fa-map-marker-alt"></i>
                              <span>{request.location?.address}</span>
                            </div>
                            <div className="detail-row">
                              <i className="fas fa-user"></i>
                              <span>Customer ID: {request.customer_id}</span>
                            </div>
                            <div className="detail-row">
                              <i className="fas fa-clock"></i>
                              <span>{formatDate(request.created_at)}</span>
                            </div>
                          </div>


                          <div className="request-actions">
                            <button
                              className="btn btn-primary"
                              onClick={() => handleAssignRequest(request.id)}
                              disabled={actionLoading === request.id}
                            >
                              {actionLoading === request.id ? (
                                <>
                                  <i className="fas fa-spinner fa-spin"></i>
                                  Assigning...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-hand-paper"></i>
                                  Pick Up Request
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'assigned' && (
                <div className="assigned-requests">
                  <h2>My Assigned Requests</h2>
                  {assignedRequests.length === 0 ? (
                    <div className="empty-state">
                      <i className="fas fa-clipboard-list"></i>
                      <p>No assigned requests.</p>
                    </div>
                  ) : (
                    <div className="requests-grid">
                      {assignedRequests.map((request) => (
                        <div key={request.id} className="request-card assigned">
                          <div className="request-header">
                            <div className="service-type">
                              <i className={getServiceTypeIcon(request.service_type)}></i>
                              <span>{getServiceTypeName(request.service_type)}</span>
                            </div>
                            <span className={`status-badge ${request.status}`}>
                              {request.status.replace('_', ' ')}
                            </span>
                          </div>

                          <div className="request-details">
                            <div className="detail-row">
                              <i className="fas fa-car"></i>
                              <span>{request.vehicle_year} {request.vehicle_make} {request.vehicle_model}</span>
                            </div>
                            <div className="detail-row">
                              <i className="fas fa-id-card"></i>
                              <span>License: {request.license_plate}</span>
                            </div>
                            <div className="detail-row">
                              <i className="fas fa-map-marker-alt"></i>
                              <span>{request.location?.address}</span>
                            </div>
                            <div className="detail-row">
                              <i className="fas fa-user"></i>
                              <span>Customer ID: {request.customer_id}</span>
                            </div>
                            <div className="detail-row">
                              <i className="fas fa-clock"></i>
                              <span>{formatDate(request.created_at)}</span>
                            </div>
                          </div>


                          <div className="request-actions">
                            {request.status === 'in_progress' && (
                              <button
                                className="btn btn-primary"
                                onClick={() => handleUpdateStatus(request.id, 'completed')}
                                disabled={actionLoading === request.id}
                              >
                                {actionLoading === request.id ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-check"></i>
                                    Mark Complete
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
